/**
 * DTOs mirroring the ACTUAL ASP.NET Core backend contracts (PinakesSSO.Application/Dtos + Endpoints).
 *
 * JSON casing: System.Text.Json default = camelCase (C# `UserName` → `userName`).
 * Enums have NO string converter, so they serialize as their numeric value:
 *   - MfaKind:   Totp = 0, Passkey = 1
 *   - UserType:  Account = 1, CallimachusUser = 2
 *   - TargetApp: Crm = 0, Export = 1, Callimachus = 2
 *
 * The SPA is a thin client: it never holds tokens, never reads the session cookie, and follows the
 * server-issued `next` instructions.
 */

/** WebAuthn / FIDO2 method kind (numeric enum on the wire). */
export enum MfaKind {
  Totp = 0,
  Passkey = 1,
}

/** End-user identity kind (numeric enum on the wire). */
export enum UserType {
  Account = 1,
  CallimachusUser = 2,
}

/** The three target applications (numeric enum on the wire). */
export enum TargetApp {
  Crm = 0,
  Export = 1,
  Callimachus = 2,
}

/** The canonical app keys used for `/go/{app}` navigation. */
export type AppKey = 'crm' | 'export' | 'callimachus';

/** Supported UI locales (source = en; targets nl, fr). */
export type Locale = 'en' | 'nl' | 'fr';

/* ---------------------------------------------------------------------------------------------- */
/* Session / boot                                                                                  */
/* ---------------------------------------------------------------------------------------------- */

/** GET /api/session — anonymous-safe session probe (SessionStateDto). */
export interface SessionState {
  authenticated: boolean;
  userType: UserType | null;
  displayName: string | null;
  mfaSatisfied: boolean;
  mfaEnrolled: boolean;
  isAdmin: boolean;
  /** Two-letter UI language from the user's stored culture (null when anonymous/unknown). */
  language: string | null;
}

/* ---------------------------------------------------------------------------------------------- */
/* Login + 2FA                                                                                     */
/* ---------------------------------------------------------------------------------------------- */

/** A 2FA method the user may use at the verification step (MfaMethodSummary). */
export interface MfaMethodSummary {
  id: number;
  kind: MfaKind;
  displayName: string | null;
  lastUsedUtc: string | null;
}

/** Discriminated result of POST /api/login/password. */
export type LoginResult =
  | { next: 'mfa'; methods: MfaMethodSummary[] }
  | { next: 'enroll'; methods: MfaKind[] }
  | { next: 'complete' }
  | { next: 'failed' };

/** Result of a second-factor verification (POST /api/login/totp | /recovery | /passkey/verify). */
export type TwoFactorResult =
  | { next: 'complete'; remainingRecoveryCodes: number | null }
  | { next: 'failed' };

/* ---------------------------------------------------------------------------------------------- */
/* MFA management (group /api/mfa)                                                                 */
/* ---------------------------------------------------------------------------------------------- */

/** POST /api/mfa/totp/enroll (TotpEnrollResult). The secret is held pending confirm. */
export interface TotpEnrollResult {
  /** Inline SVG of the otpauth QR code. */
  qrSvg: string;
  /** otpauth://totp/... — contains the secret. NEVER log this. */
  otpauthUri: string;
  /** Base32 secret for manual entry. */
  manualKey: string;
}

/** A freshly generated recovery-code set (shown once). RecoveryCodesResult. */
export interface RecoveryCodesResult {
  codes: string[];
}

/**
 * Result of confirming an enrollment (EnrollResult). Recovery codes are present (non-empty) only on
 * the FIRST successful 2FA enrollment.
 */
export interface EnrollResult {
  methodId: number;
  recoveryCodes: string[];
}

/** Result of completing a passkey registration (WebAuthnRegisterResult). */
export interface WebAuthnRegisterResult {
  methodId: number;
}

/** One MFA method as listed in the management UI (GET /api/mfa/methods → MfaMethodDto[]). */
export interface MfaMethodDto {
  id: number;
  /** Canonical DB string: 'totp' | 'passkey'. */
  kind: string;
  displayName: string | null;
  createdUtc: string;
  lastUsedUtc: string | null;
  disabled: boolean;
}

/** GET /api/mfa/recovery. */
export interface RecoveryRemaining {
  remaining: number;
}

/* ---------------------------------------------------------------------------------------------- */
/* Account lifecycle (group /api/account)                                                          */
/* ---------------------------------------------------------------------------------------------- */

/** Generic acknowledgement for enumeration-safe endpoints (forgot/reset/activate/change). */
export interface OkResult {
  ok: boolean;
}

/** GET /api/account/password-policy — server-side password rules the SPA checklists mirror. */
export interface PasswordPolicyDto {
  minLength: number;
  maxLength: number;
}

/* ---------------------------------------------------------------------------------------------- */
/* Launcher (group /api/launcher)                                                                  */
/* ---------------------------------------------------------------------------------------------- */

/** One eligible app button (LauncherApp). Display-only. */
export interface LauncherApp {
  app: TargetApp;
  displayName: string;
  launchUrl: string;
}

/** GET /api/launcher (LauncherResult). */
export interface LauncherResult {
  apps: LauncherApp[];
}

/* ---------------------------------------------------------------------------------------------- */
/* Logout                                                                                          */
/* ---------------------------------------------------------------------------------------------- */

/** One step of the front-channel signout chain (SignoutChainStep): app for display, url to walk. */
export interface SignoutStep {
  app: TargetApp;
  url: string;
}

/** POST /api/logout — the ordered front-channel signout chain. */
export interface LogoutResult {
  signoutChain: SignoutStep[];
}

/* ---------------------------------------------------------------------------------------------- */
/* Admin DTOs (group /api/admin)                                                                   */
/* ---------------------------------------------------------------------------------------------- */

/** Admin user search result row (AdminUserView). */
export interface AdminUserView {
  userType: UserType;
  id: number;
  userName: string;
  email: string | null;
  isBlocked: boolean;
  isActive: boolean;
  isLockedOut: boolean;
  mfaMethods: MfaMethodDto[];
  unusedRecoveryCodes: number;
}

/** 2FA policy editor row (TwoFactorPolicyDto). GET returns an array; PUT takes ONE. */
export interface TwoFactorPolicyDto {
  userType: UserType;
  required: boolean;
  allowTotp: boolean;
  allowPasskey: boolean;
  /** ISO 8601 or null. */
  graceUntilUtc: string | null;
}

/** Redirect-URI allowlist row (RedirectAllowlistDto). */
export interface RedirectAllowlistDto {
  id: number;
  targetApp: TargetApp;
  originExact: string;
  pathPrefix: string;
  enabled: boolean;
}

/** One audit row (AuditEventDto). */
export interface AuditEventDto {
  id: number;
  occurredUtc: string;
  eventType: string;
  userType: UserType | null;
  userName: string | null;
  targetApp: string | null;
  ip: string | null;
  detail: string | null;
}
